import type { Map as MapLibreMap, StyleImageInterface } from 'maplibre-gl';
import { STATUS_HEX } from '@/lib/vehicle-status';
import type { LiveStatus } from '@/types/api';

const DPR = 2;
const SIZE = 40;

/**
 * Pin geometry, in logical pixels. The canvas is deliberately larger than the
 * silhouette: the 2px ring and the blurred shadow both extend past the path, and
 * anything outside the canvas is clipped rather than drawn.
 */
const PIN_W = 34;
const PIN_H = 47;
const PIN_HEAD_Y = 15;
const PIN_RADIUS = 11;
const PIN_TIP_Y = 41;

/**
 * Shifts the sprite up so its tip — not its centre — lands on the coordinate.
 * MapLibre scales icon-offset by icon-size, so the tip stays put at every zoom.
 */
export const PIN_TIP_OFFSET: [number, number] = [0, PIN_H / 2 - PIN_TIP_Y];

/** Tip to head centre, in logical pixels — what hit testing has to reach up. */
export const PIN_SHAFT = PIN_TIP_Y - PIN_HEAD_Y;
export const PIN_HEAD_RADIUS = PIN_RADIUS;

function canvas2d(width = SIZE, height = SIZE) {
  const canvas = document.createElement('canvas');
  canvas.width = width * DPR;
  canvas.height = height * DPR;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(DPR, DPR);
  return { canvas, ctx };
}

/**
 * A map pin, tip down. Status colour in the head, a white ring so it reads on a
 * busy basemap, and a hole punched through the middle so it is legible as a
 * marker rather than a coloured blob at small sizes.
 *
 * Deliberately not rotated to the bearing: a pin points at its coordinate, and
 * turning one on its side reads as broken. Heading is shown as degrees in the
 * hover card and the detail panel instead.
 */
function drawPin(color: string, ring: string, shadow: string): ImageData {
  const { canvas, ctx } = canvas2d(PIN_W, PIN_H);
  const cx = PIN_W / 2;

  // Teardrop: the major arc over the top of the head, then down to the tip.
  // The tangent points sit at acos(r / d) either side of straight down, which
  // is what makes the taper meet the circle without a seam.
  const beta = Math.acos(PIN_RADIUS / (PIN_TIP_Y - PIN_HEAD_Y));
  ctx.beginPath();
  ctx.arc(cx, PIN_HEAD_Y, PIN_RADIUS, Math.PI / 2 + beta, Math.PI / 2 - beta + Math.PI * 2);
  ctx.lineTo(cx, PIN_TIP_Y);
  ctx.closePath();

  ctx.shadowColor = shadow;
  ctx.shadowBlur = 3.5;
  ctx.shadowOffsetY = 1.2;
  ctx.fillStyle = color;
  ctx.fill();

  // Reset before stroking, or the ring casts a second shadow over the fill.
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.lineWidth = 2;
  ctx.strokeStyle = ring;
  ctx.lineJoin = 'round';
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, PIN_HEAD_Y, 4.1, 0, Math.PI * 2);
  ctx.fillStyle = ring;
  ctx.fill();

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/**
 * A self-animating image. MapLibre calls render() every frame while the image is
 * on screen, which is the cheapest way to get a pulsing ring without a per-frame
 * React render or a paint-property animation loop.
 */
function pulsingRing(color: string): StyleImageInterface & { map?: MapLibreMap } {
  const size = 120;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

  return {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),

    render() {
      const duration = 1400;
      const t = (performance.now() % duration) / duration;
      const outer = (size / 2) * (0.35 + 0.65 * t);

      ctx.clearRect(0, 0, size, size);

      ctx.beginPath();
      ctx.arc(size / 2, size / 2, outer, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = Math.max(0, 1 - t) * 0.85;
      ctx.lineWidth = 3 + 4 * (1 - t);
      ctx.stroke();

      ctx.globalAlpha = 1;
      this.data = ctx.getImageData(0, 0, size, size).data as unknown as Uint8Array;

      // Keep the map repainting for as long as something is pulsing.
      (this as { map?: MapLibreMap }).map?.triggerRepaint();
      return true;
    },
  };
}

export const PIN_ICON = (status: LiveStatus) => `vt-pin-${status}`;
export const PULSE_ICON = 'vt-pulse';

/** Idempotent: safe to call again after every style (basemap) swap. */
export function registerFleetIcons(map: MapLibreMap, theme: 'light' | 'dark'): void {
  // White in both themes. A dark outline vanished into the dark basemap and
  // left the marker as a small blob of colour with no readable shape.
  const ring = '#ffffff';
  const halo = theme === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(23,26,38,0.16)';

  (['moving', 'idle', 'offline', 'alert'] as const).forEach((status) => {
    const color = STATUS_HEX[status][theme];

    const pinId = PIN_ICON(status);
    if (map.hasImage(pinId)) map.removeImage(pinId);
    map.addImage(pinId, drawPin(color, ring, halo), { pixelRatio: DPR });
  });

  if (map.hasImage(PULSE_ICON)) map.removeImage(PULSE_ICON);
  const pulse = pulsingRing(STATUS_HEX.alert[theme]);
  pulse.map = map;
  map.addImage(PULSE_ICON, pulse, { pixelRatio: 2 });
}
